import React, { useState, useEffect, useRef } from "react";
import { Menu, Trash2, Eye } from "lucide-react";
import { db } from "../../../firebase";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import StatusBadge from "../../../Components/StatusBadge";
import ConfirmationDialog from "../../../Components/ConfirmationDialogs/ConfirmationDialogs";
import SideCandidateForm from "../../../Components/SideCandidateForm";
import { useNavigate } from "react-router-dom";
import { useDelete } from "../../../hooks/useDelete";

interface JobApplicationListing {
  id: string;
  name: string;
  phoneNumber: string;
  email: string;
  qualification: string;
  position: string;
  location: string;
  experiences: string;
  status: string;
  resumeURL?: string;
}

export default function JobApplicationListing() {
  const navigate = useNavigate();
  const [jobApplications, setJobApplications] = useState<
    JobApplicationListing[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(
    null,
  );
  const [showForm, setShowForm] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);

  // 🔹 Real-time listener (live sync with Firestore)
  useEffect(() => {
    const q = query(collection(db, "job-application"), orderBy("name"));
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data: JobApplicationListing[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<JobApplicationListing, "id">),
        }));
        setJobApplications(data);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching job applications:", error);
        setLoading(false);
      },
    );

    // Cleanup listener when component unmounts
    return () => unsubscribe();
  }, []);

  // 🔹 Delete candidate
  const {
    showDeleteDialog,
    confirmDelete,
    cancelDelete,
    handleDelete: handleDeleteCandidate,
    itemToDelete: candidateToDelete,
  } = useDelete<JobApplicationListing>({
    collectionName: "job-application",
    storageField: "resumeURL",
    // JobApplication uses real-time listener, no need for refresh callback
  });

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold">Job Application Listing</h2>
      </div>

      <div className="relative h-[500px] overflow-auto border rounded">
        <table className="w-full text-sm text-center">
          <thead>
            <tr className="bg-gray-300 sticky top-0 z-10">
              <th className="p-1">No</th>
              <th className="p-1">Name</th>
              <th className="p-1">Phone Number</th>
              <th className="p-1">Email</th>
              <th className="p-1">Qualification</th>
              <th className="p-1">Position</th>
              <th className="p-1">Location</th>
              <th className="p-1">Experiences</th>
              <th className="p-1">Status</th>
              <th className="p-1">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="p-4 text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : jobApplications.length === 0 ? (
              <tr>
                <td colSpan={10} className="p-4 text-gray-500">
                  No applications found.
                </td>
              </tr>
            ) : (
              jobApplications.map((app, index) => (
                <tr key={app.id} className="border-b hover:bg-gray-100">
                  <td className="p-1">{index + 1}</td>
                  <td className="p-1 flex flex-row gap-1 items-center">
                    {app.name}
                    <button
                      onClick={() => {
                        setSelectedCandidateId(app.id);
                        setShowForm(true);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                  <td className="p-1">{app.phoneNumber}</td>
                  <td className="p-1">{app.email}</td>
                  <td className="p-1">{app.qualification}</td>
                  <td className="p-1">{app.position}</td>
                  <td className="p-1">{app.location}</td>
                  <td className="p-1">{app.experiences}</td>
                  <td className="p-1">
                    <StatusBadge status={app.status} />
                  </td>
                  <td className="p-1">
                    <button
                      onClick={() => {
                        confirmDelete(app);
                      }}
                      className="text-red-600"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && selectedCandidateId && (
        <div
          ref={formRef}
          className="fixed top-0 right-0 w-[500px] h-screen bg-white shadow-lg z-50 overflow-y-auto"
        >
          <SideCandidateForm
            candidateId={selectedCandidateId}
            onClose={() => setShowForm(false)}
            onSave={() => setShowForm(false)}
          />
        </div>
      )}

      {/* 🔹 Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showDeleteDialog}
        icon={<Trash2 size={32} className="text-red-600" />}
        title="Delete Candidate"
        message={`Are you sure you want to delete ${candidateToDelete?.name}? This action cannot be undone.`}
        onCancel={cancelDelete}
        onConfirm={handleDeleteCandidate}
      />
    </div>
  );
}
